import { useMemo, type HTMLAttributes } from 'react'
import { useRunMode } from '@/context/run-mode'
import { cn } from '@/lib/utils'

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export interface PageHeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Heading level to use when the UI is in setup mode */
  level?: 1 | 2 | 3 | 4 | 5 | 6
  /** Heading level to use when the UI is in run mode */
  runLevel?: 1 | 2 | 3 | 4 | 5 | 6
}

export function PageHeading({
  level = 1,
  runLevel = 2,
  className,
  children,
  ...props
}: PageHeadingProps) {
  const { mode } = useRunMode()
  const headingLevel = mode === 'run' ? runLevel : level
  const HeadingTag = useMemo<HeadingTag>(() => {
    const clamped = Math.min(6, Math.max(1, headingLevel)) as 1 | 2 | 3 | 4 | 5 | 6
    return `h${clamped}` as HeadingTag
  }, [headingLevel])

  return (
    <HeadingTag
      className={cn('scroll-m-20 text-3xl font-semibold tracking-tight', className)}
      {...props}
    >
      {children}
    </HeadingTag>
  )
}
